import React, { useContext, useEffect, useMemo, useState } from "react";
import { Card, Col, Row, Typography, Modal, Select, Button, Space, Statistic } from "antd";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../context/AuthContext";
import { collection, doc, onSnapshot, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { DriverContext } from "../context/DriverContext";
import { format, subDays } from "date-fns";
import {
  AreaChartOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  RobotOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { getFleetDetails } from "../services/fleetDetailsFirestore";
import PlotlyChart from "../components/fleetAssistant/PlotlyChart";
import { useFleetAssistant } from "../context/FleetAssistantContext";

const marketFleetSizeOptions = [
  { value: "1-50", labelKey: "signup.marketOptions.fleetSize1To50" },
  { value: "51-150", labelKey: "signup.marketOptions.fleetSize51To150" },
  { value: "151-500", labelKey: "signup.marketOptions.fleetSize151To500" },
  { value: "500+", labelKey: "signup.marketOptions.fleetSize500Plus" },
];
const marketScopeOptions = [
  { value: "national", labelKey: "signup.marketOptions.scopeNational" },
  { value: "international", labelKey: "signup.marketOptions.scopeInternational" },
  { value: "both", labelKey: "signup.marketOptions.scopeBoth" },
];
const marketTypeOptions = [
  { value: "containers", labelKey: "signup.marketOptions.typeContainers" },
  { value: "pickup_dropoff", labelKey: "signup.marketOptions.typePickupDropoff" },
  { value: "ftl", labelKey: "signup.marketOptions.typeFtl" },
  { value: "ltl", labelKey: "signup.marketOptions.typeLtl" },
  { value: "last_mile", labelKey: "signup.marketOptions.typeLastMile" },
  { value: "temperature_control", labelKey: "signup.marketOptions.typeTemperatureControlled" },
];
const marketIndustryOptions = [
  { value: "chemical", labelKey: "signup.marketOptions.industryChemical" },
  { value: "food", labelKey: "signup.marketOptions.industryFood" },
  { value: "goods", labelKey: "signup.marketOptions.industryGeneralGoods" },
  { value: "automotive", labelKey: "signup.marketOptions.industryAutomotive" },
  { value: "pharma", labelKey: "signup.marketOptions.industryPharma" },
  { value: "construction", labelKey: "signup.marketOptions.industryConstruction" },
  { value: "retail", labelKey: "signup.marketOptions.industryRetail" },
];

const localizeOptions = (options, t) =>
  options.map(({ value, labelKey }) => ({ value, label: t(labelKey) }));

const CHART_COLOR_PALETTE = [
  "#2563EB",
  "#16A34A",
  "#D97706",
  "#9333EA",
  "#0F766E",
  "#DC2626",
  "#0891B2",
  "#C026D3",
  "#4F46E5",
  "#65A30D",
];

const STATUS_COLOR_MAP = {
  ACTIVE: "#16A34A",
  ASSIGNED: "#2563EB",
  PENDING: "#D97706",
  FINALIZED: "#0F766E",
  CANCELED: "#DC2626",
  CANCELLED: "#DC2626",
  UNKNOWN: "#64748B",
};

const movingAverage = (values = [], windowSize = 3) =>
  values.map((_, index) => {
    const from = Math.max(0, index - windowSize + 1);
    const segment = values.slice(from, index + 1).map((value) => Number(value) || 0);
    if (!segment.length) return 0;
    return segment.reduce((sum, value) => sum + value, 0) / segment.length;
  });

export default function Welcome() {
  const { t } = useTranslation();
  const { getFleetID } = useContext(AuthContext);
  const { driversDetails } = useContext(DriverContext);
  const [loggedUser, setLoggedUser] = useState(null);
  const primaryColor = "var(--chakra-colors-gandolapp-500)";
  const [marketOpen, setMarketOpen] = useState(false);
  const [fleetSize, setFleetSize] = useState(null);
  const [fleetScope, setFleetScope] = useState(null);
  const [fleetType, setFleetType] = useState(null);
  const [industry, setIndustry] = useState(null);
  const [savingMarket, setSavingMarket] = useState(false);
  const [marketDismissed, setMarketDismissed] = useState(false);
  const fleetId = getFleetID();
  const { isAssistantOpen, toggleAssistant } = useFleetAssistant();
  const [orderCounts, setOrderCounts] = useState({
    activeOrders: 0,
    finalizedOrders: 0,
    finalizedCurve: [],
    orderStatusCounts: {},
    ordersByType: {},
    finalizedTrendLabels: [],
    finalizedTrendDaily: [],
    originDestinationHeat: {
      origins: [],
      destinations: [],
      matrix: [],
      topPairs: [],
    },
  });
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [dashboardWidgets, setDashboardWidgets] = useState([]);

  const toJsDate = (timestampLike) => {
    if (!timestampLike) return null;
    if (typeof timestampLike?.toDate === "function") return timestampLike.toDate();
    if (typeof timestampLike === "string") {
      const parsed = new Date(timestampLike);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    if (typeof timestampLike?.seconds === "number") {
      return new Date(timestampLike.seconds * 1000);
    }
    return null;
  };

  const buildFinalizedCurve = (finalizedOrders, daysCount = 30) => {
    const today = new Date();
    const days = Array.from({ length: daysCount }, (_, index) => subDays(today, daysCount - 1 - index));
    const dailyCounts = new Map();
    days.forEach((day) => dailyCounts.set(format(day, "yyyy-MM-dd"), 0));

    finalizedOrders.forEach((order) => {
      const raw = order?.raw || {};
      const finalizedAt =
        toJsDate(raw.finalizedAt) ||
        toJsDate(raw.finalized_at) ||
        toJsDate(raw.updatedAt) ||
        toJsDate(raw.updated_at);
      if (!finalizedAt) return;
      const key = format(finalizedAt, "yyyy-MM-dd");
      if (!dailyCounts.has(key)) return;
      dailyCounts.set(key, (dailyCounts.get(key) || 0) + 1);
    });

    let running = 0;
    return days.map((day) => {
      running += dailyCounts.get(format(day, "yyyy-MM-dd")) || 0;
      return running;
    });
  };

  const buildFinalizedSeries = (finalizedOrders, daysCount = 30) => {
    const today = new Date();
    const days = Array.from({ length: daysCount }, (_, index) => subDays(today, daysCount - 1 - index));
    const labels = days.map((day) => format(day, "yyyy-MM-dd"));
    const dailyMap = new Map(labels.map((label) => [label, 0]));

    finalizedOrders.forEach((order) => {
      const raw = order?.raw || {};
      const finalizedAt =
        toJsDate(raw.finalizedAt) ||
        toJsDate(raw.finalized_at) ||
        toJsDate(raw.updatedAt) ||
        toJsDate(raw.updated_at);
      if (!finalizedAt) return;
      const key = format(finalizedAt, "yyyy-MM-dd");
      if (!dailyMap.has(key)) return;
      dailyMap.set(key, (dailyMap.get(key) || 0) + 1);
    });

    return {
      labels,
      dailyValues: labels.map((label) => dailyMap.get(label) || 0),
    };
  };

  const cleanRouteLocationLabel = (rawValue = "") => {
    const normalized = String(rawValue || "")
      .replace(/\s+/g, " ")
      .trim();
    if (!normalized) return "";
    return normalized.length > 64 ? `${normalized.slice(0, 61)}...` : normalized;
  };

  const getPointLabel = (point = {}) => {
    if (!point || typeof point !== "object") return "";
    const candidates = [point.name, point.address, point.label, point.locationName];
    for (const candidate of candidates) {
      const label = cleanRouteLocationLabel(candidate);
      if (label) return label;
    }
    const latitude = Number(point.latitude);
    const longitude = Number(point.longitude);
    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      return `${latitude.toFixed(3)}, ${longitude.toFixed(3)}`;
    }
    return "";
  };

  const getOrderOriginDestination = (raw = {}) => {
    const checkPoints = Array.isArray(raw?.checkPoints)
      ? raw.checkPoints
      : Array.isArray(raw?.checkpoints)
        ? raw.checkpoints
        : [];
    const points = raw?.truckflow?.points || {};

    let origin = "";
    let destination = "";

    if (checkPoints.length > 0) {
      origin = getPointLabel(checkPoints[0]);
      destination = getPointLabel(checkPoints[checkPoints.length - 1]);
    }

    if (!origin) {
      origin =
        getPointLabel(points.origin || {}) ||
        cleanRouteLocationLabel(raw?.transfollow?.placeOfTakingOver?.name || "");
    }
    if (!destination) {
      destination =
        getPointLabel(points.customer || points.delivery || points.destination || points.gateIn || {}) ||
        cleanRouteLocationLabel(raw?.transfollow?.placeOfDelivery?.name || "");
    }

    return { origin, destination };
  };

  const buildOriginDestinationHeatData = (orders = [], limit = 8) => {
    const pairCounts = {};
    const originCounts = {};
    const destinationCounts = {};

    orders.forEach((order) => {
      const { origin, destination } = getOrderOriginDestination(order?.raw || {});
      if (origin) originCounts[origin] = (originCounts[origin] || 0) + 1;
      if (destination) destinationCounts[destination] = (destinationCounts[destination] || 0) + 1;
      if (origin && destination) {
        const key = `${origin}|||${destination}`;
        pairCounts[key] = (pairCounts[key] || 0) + 1;
      }
    });

    const sortedOrigins = Object.entries(originCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit)
      .map((entry) => entry[0]);
    const sortedDestinations = Object.entries(destinationCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit)
      .map((entry) => entry[0]);

    const matrix = sortedOrigins.map((origin) =>
      sortedDestinations.map((destination) => pairCounts[`${origin}|||${destination}`] || 0)
    );
    const topPairs = Object.entries(pairCounts)
      .map(([pair, value]) => {
        const [origin, destination] = pair.split("|||");
        return { origin, destination, value: Number(value) || 0 };
      })
      .sort((a, b) => b.value - a.value)
      .slice(0, 12);

    return {
      origins: sortedOrigins,
      destinations: sortedDestinations,
      matrix,
      topPairs,
    };
  };

  const MiniArea = ({ values, height = 110 }) => {
    if (!Array.isArray(values) || values.length < 2) {
      return (
        <div style={{ height, display: "grid", placeItems: "center", color: "#6b7280", fontSize: 12 }}>
          {t("driverDetail.none", { defaultValue: "None" })}
        </div>
      );
    }

    const width = 520;
    const paddingX = 8;
    const paddingY = 10;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = Math.max(1, max - min);

    const points = values.map((value, index) => {
      const x = paddingX + (index / (values.length - 1)) * (width - paddingX * 2);
      const y = paddingY + (1 - (value - min) / range) * (height - paddingY * 2);
      return { x, y };
    });

    const linePath = points
      .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
      .join(" ");
    const areaPath = `${linePath} L ${points[points.length - 1].x.toFixed(2)} ${(height - paddingY).toFixed(
      2
    )} L ${points[0].x.toFixed(2)} ${(height - paddingY).toFixed(2)} Z`;

    return (
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={t("home.curve.finalizedOrdersAriaLabel")}
      >
        <path d={areaPath} fill="rgba(28, 231, 131, 0.18)" />
        <path d={linePath} fill="none" stroke="rgba(17, 24, 39, 0.85)" strokeWidth="2" />
      </svg>
    );
  };

  const getLoggedUser = async () => {
    try {
      const data = await getFleetDetails(fleetId);
      setLoggedUser(data?.name || null);
    } catch (error) {
      console.error("Error al obtener detalles del fleet", error);
    }
  };

  useEffect(() => {
    if (!fleetId) return;
    getLoggedUser();
  }, [fleetId]);

  useEffect(() => {
    if (!fleetId) return undefined;
    setOrdersLoading(true);

    const ordersRef = collection(db, "fleets", fleetId, "transport_orders");
    const unsubscribe = onSnapshot(
      ordersRef,
      (snapshot) => {
        const orders = snapshot.docs.map((orderDoc) => ({ id: orderDoc.id, raw: orderDoc.data() }));
        const activeOrders = orders.filter((order) => String(order?.raw?.status || "").toUpperCase() === "ACTIVE").length;
        const finalizedOrdersList = orders.filter(
          (order) => String(order?.raw?.status || "").toUpperCase() === "FINALIZED"
        );
        const finalizedOrders = finalizedOrdersList.length;
        const finalizedCurve = buildFinalizedCurve(finalizedOrdersList, 30);
        const finalizedSeries = buildFinalizedSeries(finalizedOrdersList, 30);
        const orderStatusCounts = {};
        const ordersByType = {};
        const originDestinationHeat = buildOriginDestinationHeatData(orders, 8);

        orders.forEach((order) => {
          const status = String(order?.raw?.status || "UNKNOWN").toUpperCase();
          orderStatusCounts[status] = (orderStatusCounts[status] || 0) + 1;
          const type = String(order?.raw?.type || "UNSPECIFIED").trim() || "UNSPECIFIED";
          ordersByType[type] = (ordersByType[type] || 0) + 1;
        });

        setOrderCounts({
          activeOrders,
          finalizedOrders,
          finalizedCurve,
          orderStatusCounts,
          ordersByType,
          finalizedTrendLabels: finalizedSeries.labels,
          finalizedTrendDaily: finalizedSeries.dailyValues,
          originDestinationHeat,
        });
        setOrdersLoading(false);
      },
      (error) => {
        console.error("Failed to listen to transport orders for KPIs:", error);
        setOrderCounts({
          activeOrders: 0,
          finalizedOrders: 0,
          finalizedCurve: [],
          orderStatusCounts: {},
          ordersByType: {},
          finalizedTrendLabels: [],
          finalizedTrendDaily: [],
          originDestinationHeat: {
            origins: [],
            destinations: [],
            matrix: [],
            topPairs: [],
          },
        });
        setOrdersLoading(false);
      }
    );

    return () => unsubscribe();
  }, [fleetId]);

  useEffect(() => {
    if (!fleetId) return undefined;

    const customizationRef = doc(db, "fleets", fleetId, "customizations", "welcome_dashboard");
    const unsubscribe = onSnapshot(
      customizationRef,
      (snapshot) => {
        const data = snapshot.exists() ? snapshot.data() : {};
        const widgets = Array.isArray(data?.widgets) ? data.widgets : [];
        setDashboardWidgets(widgets);
      },
      (error) => {
        console.error("Failed to load assistant dashboard customizations:", error);
        setDashboardWidgets([]);
      }
    );

    return () => unsubscribe();
  }, [fleetId]);

  const driverTagCounts = useMemo(() => {
    const counts = {};
    (driversDetails || []).forEach((driver) => {
      (driver?.tags || []).forEach((tag) => {
        const key = String(tag || "").trim();
        if (!key) return;
        counts[key] = (counts[key] || 0) + 1;
      });
    });
    return counts;
  }, [driversDetails]);

  const kpiValues = useMemo(
    () => ({
      activeOrders: orderCounts.activeOrders,
      finalizedOrders: orderCounts.finalizedOrders,
      activeDrivers: (driversDetails || []).filter((driver) => !!driver.currentOrder).length,
      idleDrivers: (driversDetails || []).filter((driver) => !driver.currentOrder).length,
      totalDrivers: (driversDetails || []).length,
    }),
    [driversDetails, orderCounts.activeOrders, orderCounts.finalizedOrders]
  );
  const hasCustomDashboard = dashboardWidgets.length > 0;

  const normalizeChartVisualization = (value) => {
    const normalized = String(value || "")
      .trim()
      .toLowerCase();
    if (normalized.includes("bar")) return "bar";
    if (normalized.includes("line")) return "line";
    if (normalized.includes("pie") || normalized.includes("donut")) return "pie";
    if (normalized.includes("heatmap") || normalized.includes("heat map") || normalized.includes("mapa de calor")) {
      return "heatmap";
    }
    return "auto";
  };

  const pickColors = (count = 0, offset = 0) =>
    Array.from({ length: count }, (_, index) => CHART_COLOR_PALETTE[(index + offset) % CHART_COLOR_PALETTE.length]);

  const getTotal = (values = []) => values.reduce((sum, value) => sum + (Number(value) || 0), 0);

  const buildDonutCenterAnnotation = (label, total) => ({
    text: `<b>${label}</b><br><span style="font-size:16px">${total}</span>`,
    showarrow: false,
    x: 0.5,
    y: 0.5,
    xref: "paper",
    yref: "paper",
    font: { size: 11, color: "#0F172A" },
    align: "center",
  });

  const buildEmptyFigure = (message) => ({
    data: [],
    layout: {
      paper_bgcolor: "rgba(0,0,0,0)",
      plot_bgcolor: "rgba(0,0,0,0)",
      margin: { t: 8, r: 8, b: 12, l: 8 },
      xaxis: { visible: false },
      yaxis: { visible: false },
      annotations: [
        {
          text: message,
          showarrow: false,
          x: 0.5,
          y: 0.5,
          xref: "paper",
          yref: "paper",
          font: { size: 13, color: "#64748B" },
        },
      ],
    },
  });

  const buildBaseLayout = ({
    margin = { t: 8, r: 10, b: 44, l: 44 },
    showLegend = false,
    xaxis = {},
    yaxis = {},
    ...rest
  } = {}) => ({
    paper_bgcolor: "rgba(0,0,0,0)",
    plot_bgcolor: "rgba(0,0,0,0)",
    margin,
    hoverlabel: { bgcolor: "#0F172A", font: { color: "#F8FAFC" } },
    font: { size: 12, color: "#0F172A" },
    showlegend: showLegend,
    xaxis: {
      automargin: true,
      gridcolor: "rgba(148, 163, 184, 0.22)",
      zerolinecolor: "rgba(148, 163, 184, 0.28)",
      tickfont: { size: 11, color: "#334155" },
      ...xaxis,
    },
    yaxis: {
      automargin: true,
      gridcolor: "rgba(148, 163, 184, 0.22)",
      zerolinecolor: "rgba(148, 163, 184, 0.28)",
      tickfont: { size: 11, color: "#334155" },
      rangemode: "tozero",
      ...yaxis,
    },
    ...rest,
  });

  const formatOrderStatusLabel = (status) => {
    const normalized = String(status || "UNKNOWN").trim().toUpperCase();
    const key = normalized.toLowerCase();
    return t(`transportManagement.${key}`, {
      defaultValue: `${key.charAt(0).toUpperCase()}${key.slice(1)}`,
    });
  };

  const formatOrderTypeLabel = (rawType) => {
    const type = String(rawType || "UNSPECIFIED").trim().toUpperCase();
    if (type === "CONTAINER_OUTBOUND") {
      return t("orderType.containerOutbound", { defaultValue: "Container Export" });
    }
    if (type === "CONTAINER_INBOUND") {
      return t("orderType.containerInbound", { defaultValue: "Container Import" });
    }
    if (type === "CONVENTIONAL_PICKUP") {
      return t("orderType.conventionalPickup", { defaultValue: "Conventional Pickup" });
    }
    if (type === "CONVENTIONAL_DELIVERY") {
      return t("orderType.conventionalDelivery", { defaultValue: "Conventional Delivery" });
    }
    return type.replace(/_/g, " ");
  };

  const buildWidgetFigure = (chartKey, visualization = "auto") => {
    const requestedVisualization = normalizeChartVisualization(visualization);

    if (chartKey === "orderStatusDistribution") {
      const entries = Object.entries(orderCounts.orderStatusCounts || {})
        .map(([status, value]) => [status, Number(value) || 0])
        .sort((a, b) => b[1] - a[1]);
      if (!entries.length) {
        return buildEmptyFigure(t("driverDetail.none", { defaultValue: "No data" }));
      }

      const statusKeys = entries.map(([status]) => status);
      const labels = statusKeys.map((status) => formatOrderStatusLabel(status));
      const values = entries.map((entry) => entry[1]);
      const colors = statusKeys.map((status, index) => STATUS_COLOR_MAP[status] || pickColors(1, index)[0]);

      if (requestedVisualization === "bar") {
        return {
          data: [
            {
              type: "bar",
              x: labels,
              y: values,
              marker: {
                color: colors,
                line: { color: "rgba(255,255,255,0.75)", width: 1.2 },
              },
              text: values.map((value) => String(value)),
              textposition: "outside",
              cliponaxis: false,
              hovertemplate: "%{x}<br>%{y} orders<extra></extra>",
            },
          ],
          layout: buildBaseLayout({
            margin: { t: 8, r: 10, b: 58, l: 40 },
            xaxis: { tickangle: -15, type: "category" },
            yaxis: { dtick: 1 },
          }),
        };
      }
      return {
        data: [
          {
            type: "pie",
            labels,
            values,
            hole: 0.45,
            marker: { colors, line: { color: "#FFFFFF", width: 2 } },
            textinfo: "label+percent",
            hovertemplate: "%{label}<br>%{value} orders (%{percent})<extra></extra>",
            sort: false,
          },
        ],
        layout: buildBaseLayout({
          margin: { t: 8, r: 8, b: 8, l: 8 },
          showLegend: true,
          xaxis: { visible: false },
          yaxis: { visible: false },
          legend: { orientation: "h", y: -0.08, x: 0, font: { size: 11 } },
          annotations: [
            buildDonutCenterAnnotation(
              t("common.total", { defaultValue: "Total" }),
              getTotal(values)
            ),
          ],
        }),
      };
    }

    if (chartKey === "ordersByType") {
      const entries = Object.entries(orderCounts.ordersByType || {})
        .map(([type, value]) => [type, Number(value) || 0])
        .sort((a, b) => b[1] - a[1]);
      if (!entries.length) {
        return buildEmptyFigure(t("driverDetail.none", { defaultValue: "No data" }));
      }

      const labels = entries.map((entry) => formatOrderTypeLabel(entry[0]));
      const values = entries.map((entry) => entry[1]);
      const colors = pickColors(entries.length, 1);
      return {
        data: [
          {
            type: "bar",
            orientation: "h",
            y: labels,
            x: values,
            marker: {
              color: colors,
              line: { color: "rgba(255,255,255,0.75)", width: 1.2 },
            },
            text: values.map((value) => String(value)),
            textposition: "outside",
            cliponaxis: false,
            hovertemplate: "%{y}<br>%{x} orders<extra></extra>",
          },
        ],
        layout: buildBaseLayout({
          margin: { t: 8, r: 32, b: 20, l: 150 },
          xaxis: { dtick: 1, title: { text: t("transportManagement.orders", { defaultValue: "Orders" }) } },
          yaxis: { autorange: "reversed", categoryorder: "array", categoryarray: labels },
        }),
      };
    }

    if (chartKey === "finalizedTrend") {
      const labels = orderCounts.finalizedTrendLabels || [];
      const values = orderCounts.finalizedTrendDaily || [];
      if (!labels.length || !values.length) {
        return buildEmptyFigure(t("driverDetail.none", { defaultValue: "No data" }));
      }

      if (requestedVisualization === "bar") {
        return {
          data: [
            {
              type: "bar",
              x: labels,
              y: values,
              marker: {
                color: pickColors(values.length, 2),
                line: { color: "rgba(255,255,255,0.7)", width: 0.8 },
              },
              hovertemplate: "%{x}<br>%{y} finalized orders<extra></extra>",
            },
          ],
          layout: buildBaseLayout({
            margin: { t: 8, r: 10, b: 48, l: 40 },
            xaxis: { tickangle: -30, tickmode: "auto", nticks: 8 },
            yaxis: { dtick: 1 },
          }),
        };
      }
      return {
        data: [
          {
            type: "scatter",
            mode: "lines+markers",
            x: labels,
            y: values,
            name: t("home.kpis.finalizedOrders", { defaultValue: "Finalized Orders" }),
            line: { color: "#2563EB", width: 3, shape: "spline", smoothing: 0.25 },
            marker: { color: "#0F172A", size: 6, line: { color: "#FFFFFF", width: 1 } },
            fill: "tozeroy",
            fillcolor: "rgba(37, 99, 235, 0.16)",
            hovertemplate: "%{x}<br>%{y} finalized orders<extra></extra>",
          },
          {
            type: "scatter",
            mode: "lines",
            x: labels,
            y: movingAverage(values, 3),
            line: { color: "#D97706", width: 2, dash: "dash" },
            hovertemplate: "%{x}<br>%{y:.1f} avg (3d)<extra></extra>",
            name: t("fleetAssistant.movingAverage3d", { defaultValue: "3-day average" }),
          },
        ],
        layout: buildBaseLayout({
          margin: { t: 8, r: 10, b: 48, l: 40 },
          xaxis: { tickangle: -30, tickmode: "auto", nticks: 8 },
          yaxis: { dtick: 1 },
          showLegend: true,
          legend: { orientation: "h", y: -0.2, x: 0, font: { size: 11 } },
        }),
      };
    }

    if (chartKey === "driverActivity") {
      const labels = [
        t("home.kpis.activeDrivers", { defaultValue: "Active Drivers" }),
        t("home.kpis.idleDrivers", { defaultValue: "Idle Drivers" }),
      ];
      const values = [kpiValues.activeDrivers, kpiValues.idleDrivers];
      const colors = ["#16A34A", "#64748B"];

      if (requestedVisualization === "bar") {
        return {
          data: [
            {
              type: "bar",
              x: labels,
              y: values,
              marker: {
                color: colors,
                line: { color: "rgba(255,255,255,0.75)", width: 1.2 },
              },
              text: values.map((value) => String(value)),
              textposition: "outside",
              cliponaxis: false,
              hovertemplate: "%{x}<br>%{y} drivers<extra></extra>",
            },
          ],
          layout: buildBaseLayout({
            margin: { t: 8, r: 10, b: 54, l: 40 },
            xaxis: { tickangle: -10 },
            yaxis: { dtick: 1 },
          }),
        };
      }
      return {
        data: [
          {
            type: "pie",
            labels,
            values,
            hole: 0.4,
            marker: { colors, line: { color: "#FFFFFF", width: 2 } },
            textinfo: "label+percent",
            hovertemplate: "%{label}<br>%{value} drivers (%{percent})<extra></extra>",
          },
        ],
        layout: buildBaseLayout({
          margin: { t: 8, r: 8, b: 8, l: 8 },
          showLegend: true,
          xaxis: { visible: false },
          yaxis: { visible: false },
          legend: { orientation: "h", y: -0.08, x: 0, font: { size: 11 } },
          annotations: [
            buildDonutCenterAnnotation(
              t("common.total", { defaultValue: "Total" }),
              getTotal(values)
            ),
          ],
        }),
      };
    }

    if (chartKey === "topDriverTags") {
      const entries = Object.entries(driverTagCounts)
        .map(([tag, value]) => [tag, Number(value) || 0])
        .sort((a, b) => b[1] - a[1])
        .slice(0, 8);
      if (!entries.length) {
        return buildEmptyFigure(t("driverDetail.none", { defaultValue: "No data" }));
      }

      const labels = entries.map((entry) => entry[0]);
      const values = entries.map((entry) => entry[1]);
      const colors = pickColors(entries.length, 3);
      return {
        data: [
          {
            type: "bar",
            orientation: "h",
            y: labels,
            x: values,
            marker: {
              color: colors,
              line: { color: "rgba(255,255,255,0.75)", width: 1.2 },
            },
            text: values.map((value) => String(value)),
            textposition: "outside",
            cliponaxis: false,
            hovertemplate: "%{y}<br>%{x} drivers<extra></extra>",
          },
          {
            type: "scatter",
            mode: "markers",
            y: labels,
            x: values,
            marker: {
              size: 11,
              color: "#0F172A",
              line: { color: "#FFFFFF", width: 1.5 },
            },
            hovertemplate: "%{y}<br>%{x} drivers<extra></extra>",
            showlegend: false,
          },
        ],
        layout: buildBaseLayout({
          margin: { t: 8, r: 32, b: 22, l: 140 },
          xaxis: { dtick: 1, title: { text: t("orderDetail.driver", { defaultValue: "Drivers" }) } },
          yaxis: { autorange: "reversed", categoryorder: "array", categoryarray: labels },
        }),
      };
    }

    if (chartKey === "originDestinationHeatmap") {
      const heat = orderCounts.originDestinationHeat || {};
      const origins = Array.isArray(heat.origins) ? heat.origins : [];
      const destinations = Array.isArray(heat.destinations) ? heat.destinations : [];
      const matrix = Array.isArray(heat.matrix) ? heat.matrix : [];
      const topPairs = Array.isArray(heat.topPairs) ? heat.topPairs : [];
      if (!origins.length || !destinations.length || !matrix.length) {
        return buildEmptyFigure(t("driverDetail.none", { defaultValue: "No data" }));
      }

      if (requestedVisualization === "bar") {
        const barEntries = topPairs.slice(0, 10);
        if (!barEntries.length) {
          return buildEmptyFigure(t("driverDetail.none", { defaultValue: "No data" }));
        }
        const labels = barEntries.map((entry) => `${entry.origin} -> ${entry.destination}`);
        const values = barEntries.map((entry) => entry.value);
        return {
          data: [
            {
              type: "bar",
              orientation: "h",
              y: labels,
              x: values,
              marker: {
                color: pickColors(values.length, 5),
                line: { color: "rgba(255,255,255,0.75)", width: 1.2 },
              },
              text: values.map((value) => String(value)),
              textposition: "outside",
              cliponaxis: false,
              hovertemplate: "%{y}<br>%{x} orders<extra></extra>",
            },
          ],
          layout: buildBaseLayout({
            margin: { t: 8, r: 36, b: 20, l: 220 },
            xaxis: { dtick: 1, title: { text: t("transportManagement.orders", { defaultValue: "Orders" }) } },
            yaxis: { autorange: "reversed", categoryorder: "array", categoryarray: labels },
          }),
        };
      }

      return {
        data: [
          {
            type: "heatmap",
            x: destinations,
            y: origins,
            z: matrix,
            colorscale: [
              [0, "#F8FAFC"],
              [0.2, "#DBEAFE"],
              [0.45, "#93C5FD"],
              [0.7, "#3B82F6"],
              [1, "#1D4ED8"],
            ],
            reversescale: false,
            colorbar: {
              title: { text: t("transportManagement.orders", { defaultValue: "Orders" }) },
              thickness: 12,
            },
            hovertemplate:
              `${t("mapEditor.pickUp", { defaultValue: "Pickup Location" })}: %{y}<br>` +
              `${t("mapEditor.delivery", { defaultValue: "Delivery Location" })}: %{x}<br>` +
              `${t("transportManagement.orders", { defaultValue: "Orders" })}: %{z}<extra></extra>`,
          },
        ],
        layout: buildBaseLayout({
          margin: { t: 8, r: 14, b: 96, l: 120 },
          xaxis: { tickangle: -30, side: "bottom", type: "category" },
          yaxis: { type: "category", autorange: "reversed" },
        }),
      };
    }

    return null;
  };

  useEffect(() => {
    const pending = localStorage.getItem("marketResearchPending");
    const status = localStorage.getItem("marketResearchStatus");
    if (!fleetId || marketDismissed) return;
    if (pending && pending === fleetId && status === "later") {
      setMarketOpen(true);
    }
  }, [fleetId, marketDismissed]);

  const handleSkipMarket = async () => {
    if (fleetId) {
      try {
        await setDoc(
          doc(db, "fleets", fleetId),
          { marketResearchStatus: "skipped" },
          { merge: true }
        );
      } catch (error) {
        console.error("Failed to save market research skip:", error);
      }
    }
    localStorage.setItem("marketResearchStatus", "skipped");
    localStorage.removeItem("marketResearchPending");
    setMarketDismissed(true);
    setMarketOpen(false);
  };

  const handleLaterMarket = () => {
    localStorage.setItem("marketResearchStatus", "later");
    setMarketDismissed(true);
    setMarketOpen(false);
  };

  const handleSaveMarket = async () => {
    if (!fleetId) {
      handleSkipMarket();
      return;
    }
    setSavingMarket(true);
    try {
      await setDoc(
        doc(db, "fleets", fleetId),
        {
          marketResearch: {
            fleetSize,
            scope: fleetScope,
            type: fleetType,
            industry,
          },
          marketResearchStatus: "completed",
        },
        { merge: true }
      );
    } catch (error) {
      console.error("Failed to save market research:", error);
    } finally {
      localStorage.setItem("marketResearchStatus", "completed");
      localStorage.removeItem("marketResearchPending");
      setSavingMarket(false);
      setMarketDismissed(true);
      setMarketOpen(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", padding: "48px 24px" }}>
      <div style={{ textAlign: "center", marginBottom: 48 }}>
        <Typography.Title level={2} style={{ color: primaryColor }}>
          {t("welcome.welcome")}
          {loggedUser ? `, ${loggedUser}!` : "!"}
        </Typography.Title>
        <Space size={10} wrap>
          <Button
            type={isAssistantOpen ? "default" : "primary"}
            icon={<RobotOutlined />}
            onClick={toggleAssistant}
          >
            {isAssistantOpen
              ? t("fleetAssistant.close", { defaultValue: "Close Fleet Assistant" })
              : t("fleetAssistant.open", { defaultValue: "Open Fleet Assistant" })}
          </Button>
        </Space>
      </div>

      {hasCustomDashboard ? (
        <Card bordered={false} style={{ borderRadius: 16, marginBottom: 20 }}>
          <Space direction="vertical" style={{ width: "100%" }} size={12}>
            <Typography.Text strong>
              {t("fleetAssistant.assistantDashboard", { defaultValue: "Assistant dashboard" })}
            </Typography.Text>
            <Row gutter={[12, 12]}>
              {dashboardWidgets.map((widget) => {
                if (widget?.type === "kpi") {
                  const value = kpiValues[widget.metricKey] ?? 0;
                  return (
                    <Col key={widget.id} xs={24} md={8}>
                      <Card bordered={false} style={{ borderRadius: 12 }}>
                        <Statistic title={widget.title || widget.metricKey} value={value} />
                      </Card>
                    </Col>
                  );
                }

                if (widget?.type === "chart") {
                  const figure = buildWidgetFigure(widget.chartKey, widget.visualization);
                  if (!figure) return null;
                  return (
                    <Col key={widget.id} xs={24} md={12}>
                      <Card bordered={false} style={{ borderRadius: 12 }}>
                        <Space direction="vertical" style={{ width: "100%" }} size={6}>
                          <Typography.Text strong>{widget.title || widget.chartKey}</Typography.Text>
                          <PlotlyChart figure={figure} height={260} />
                        </Space>
                      </Card>
                    </Col>
                  );
                }

                return null;
              })}
            </Row>
          </Space>
        </Card>
      ) : null}

      {!hasCustomDashboard ? (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} md={6}>
              <Card bordered={false} style={{ borderRadius: 16 }}>
                <Statistic
                  title={t("home.kpis.activeOrders")}
                  value={orderCounts.activeOrders}
                  loading={ordersLoading}
                  prefix={<ThunderboltOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} md={6}>
              <Card bordered={false} style={{ borderRadius: 16 }}>
                <Statistic
                  title={t("home.kpis.finalizedOrders")}
                  value={orderCounts.finalizedOrders}
                  loading={ordersLoading}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} md={6}>
              <Card bordered={false} style={{ borderRadius: 16 }}>
                <Statistic
                  title={t("home.kpis.idleDrivers")}
                  value={(driversDetails || []).filter((driver) => !driver.currentOrder).length}
                  prefix={<ClockCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} md={6}>
              <Card bordered={false} style={{ borderRadius: 16 }}>
                <Statistic
                  title={t("home.kpis.activeDrivers")}
                  value={(driversDetails || []).filter((driver) => !!driver.currentOrder).length}
                  prefix={<AreaChartOutlined />}
                />
              </Card>
            </Col>
          </Row>

          <Card bordered={false} style={{ borderRadius: 16, marginBottom: 28 }}>
            <Space direction="vertical" style={{ width: "100%" }} size={10}>
              <Typography.Text strong style={{ fontSize: 14 }}>
                {t("home.curve.finalizedAccumulated")}
              </Typography.Text>
              <MiniArea values={orderCounts.finalizedCurve} />
            </Space>
          </Card>
        </>
      ) : null}

      <Modal
        title={t("signup.marketResearch", { defaultValue: "Market research (optional)" })}
        open={marketOpen}
        onCancel={handleLaterMarket}
        footer={[
          <Button key="later" onClick={handleLaterMarket}>
            {t("signup.later", { defaultValue: "Do later" })}
          </Button>,
          <Button key="skip" onClick={handleSkipMarket}>
            {t("signup.skip", { defaultValue: "Skip" })}
          </Button>,
          <Button key="save" type="primary" loading={savingMarket} onClick={handleSaveMarket}>
            {t("signup.save", { defaultValue: "Save" })}
          </Button>,
        ]}
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(200px, 1fr))", gap: 12 }}>
          <Select
            allowClear
            placeholder={t("signup.fleetSize", { defaultValue: "Fleet size" })}
            options={localizeOptions(marketFleetSizeOptions, t)}
            value={fleetSize}
            onChange={setFleetSize}
          />
          <Select
            allowClear
            placeholder={t("signup.scope", { defaultValue: "Scope" })}
            options={localizeOptions(marketScopeOptions, t)}
            value={fleetScope}
            onChange={setFleetScope}
          />
          <Select
            allowClear
            placeholder={t("signup.type", { defaultValue: "Type" })}
            options={localizeOptions(marketTypeOptions, t)}
            value={fleetType}
            onChange={setFleetType}
          />
          <Select
            allowClear
            placeholder={t("signup.industry", { defaultValue: "Industry" })}
            options={localizeOptions(marketIndustryOptions, t)}
            value={industry}
            onChange={setIndustry}
          />
        </div>
      </Modal>
    </div>
  );
}
